import { getTimezone } from '@utils/common'
import { CalendarOptions, CalendarEvent } from '.'

// FUNCTION TO GENERATE THE GOOGLE URL
// See specs at: https://github.com/InteractionDesignFoundation/add-event-to-calendar-docs/blob/main/services/google.md (unofficial)
export function generateGoogleUrl(startDate: Date, endDate: Date, name: string, description: string): string {
  const urlParts = []
  urlParts.push('https://calendar.google.com/calendar/render?action=TEMPLATE')

  const start = new Date(startDate.getTime())
  const end = new Date(endDate.getTime())
  const offsetMins = startDate.getTimezoneOffset()

  start.setMinutes(startDate.getMinutes() - offsetMins)
  end.setMinutes(endDate.getMinutes() - offsetMins)

  urlParts.push(
    'dates=' +
      encodeURIComponent(
        start
          .toISOString()
          .replace(/(-|:|(\.\d{3}))/g, '')
          .replace('Z', '')
      ) +
      '%2F' +
      encodeURIComponent(
        end
          .toISOString()
          .replace(/(-|:|(\.\d{3}))/g, '')
          .replace('Z', '')
      )
  )

  urlParts.push(`ctz=${getTimezone(start)}`)
  urlParts.push('text=' + encodeURIComponent(name))
  urlParts.push('details=' + encodeURIComponent(description))

  return urlParts.join('&')
}

// FUNCTION TO GENERATE THE iCAL FILE AND URL
// See specs at: https://www.rfc-editor.org/rfc/rfc5545.html
export function generateICalUrl(
  startDate: Date,
  endDate: Date,
  name: string,
  description: string,
  eventUrl: string,
  useDataUrl: boolean
): string {
  const body: string[] = []

  body.push(`DTSTART:${startDate.toISOString().replace(/(-|:|(\.\d{3}))/g, '')}`)
  body.push(`SUMMARY:${name}`)
  body.push(`URL:${eventUrl}`)
  body.push(`DTEND:${endDate.toISOString().replace(/(-|:|(\.\d{3}))/g, '')}`)
  body.push(`DESCRIPTION:${description}`)

  const url = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'BEGIN:VEVENT', body.join('\n'), 'END:VEVENT', 'END:VCALENDAR'].join(
    '\n'
  )

  if (useDataUrl) {
    return encodeURI(`data:text/calendar;charset=utf8,${url}`)
  } else {
    return url
  }
}

export function isIOSSafari(): boolean {
  const ua = window.navigator.userAgent
  const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i)
  const webkit = !!ua.match(/WebKit/i)

  return iOS && webkit && !ua.match(/CriOS/i)
}

// FUNCTION TO GENERATE THE OUTLOOK URL
// See specs at: https://interactiondesignfoundation.github.io/add-event-to-calendar-docs/services/outlook-web.html
export function generateOutlookUrl(startDate: Date, endDate: Date, name: string, description: string): string {
  const urlParts = []
  const start = new Date(startDate.getTime())
  const end = new Date(endDate.getTime())
  const offsetMins = startDate.getTimezoneOffset()

  start.setMinutes(startDate.getMinutes() + offsetMins)
  end.setMinutes(endDate.getMinutes() + offsetMins)

  urlParts.push('https://outlook.live.com/calendar/action/compose?rru=addevent')
  urlParts.push('startdt=' + start.toISOString().replace(/(\.\d{3})/g, ''))
  urlParts.push('enddt=' + end.toISOString().replace(/(\.\d{3})/g, ''))

  urlParts.push('subject=' + encodeURIComponent(name))
  urlParts.push('body=' + encodeURIComponent(description))
  return urlParts.join('&')
}

// FUNCTION TO GENERATE THE YAHOO URL
// See specs at: https://github.com/InteractionDesignFoundation/add-event-to-calendar-docs/blob/main/services/yahoo.md (unofficial)
export function generateYahooUrl(startDate: Date, endDate: Date, name: string, description: string): string {
  const urlParts = []
  urlParts.push('https://calendar.yahoo.com/?v=60')
  // generate and add date
  urlParts.push(
    'st=' +
      encodeURIComponent(startDate.toISOString().replace(/(-|:|(\.\d{3}))/g, '')) +
      '&et=' +
      encodeURIComponent(endDate.toISOString().replace(/(-|:|(\.\d{3}))/g, ''))
  )

  urlParts.push('title=' + encodeURIComponent(name))

  urlParts.push('desc=' + encodeURIComponent(description))

  return urlParts.join('&')
}

export const handleCalendar = (calendarApp: CalendarOptions, selectedEvents: CalendarEvent[]) => {
  if (selectedEvents.length === 0) return
  if (calendarApp === CalendarOptions.iCal) {
    for (const event of selectedEvents) {
      const shortDescription = event.description.split('\n')[0]
      const filename = `${event.name.replace(' ', '-')}.ics`
      const url: string = generateICalUrl(
        event.startTime,
        event.endTime,
        event.name,
        shortDescription, // Note: for some reason iCal can't handle the long project descriptions, so just passing url here
        event.url ?? '',
        isIOSSafari()
      )

      // Mobile (iOS)
      if (isIOSSafari()) {
        window.open(url, '_blank')
        return
      }

      // Desktop
      const blob: Blob = new Blob([url], {
        type: 'text/calendar;charset=utf-8'
      })
      const linkEl = document.createElement('a')
      linkEl.href = window.URL.createObjectURL(blob)
      linkEl.setAttribute('download', filename)
      document.body.appendChild(linkEl)
      linkEl.click()
      document.body.removeChild(linkEl)
      console.log('success')
    }
    return
  }

  openMultipleUrls(
    selectedEvents.map((event) => {
      const shortDescription = event.description.split('\n')[0]
      switch (calendarApp) {
        case CalendarOptions.google:
          return generateGoogleUrl(event.startTime, event.endTime, event.name, event.description)
        case CalendarOptions.outlook:
          return generateOutlookUrl(event.startTime, event.endTime, event.name, shortDescription) // Note: for some reason Outlook can't handle the long project descriptions, so just passing url here
        case CalendarOptions.yahoo:
          return generateYahooUrl(event.startTime, event.endTime, event.name, event.description)
      }
    })
  )
}

const openMultipleUrls = (urls: string[]) => {
  urls.forEach((url) => {
    window.open(url, '_blank')
  })
}
